import React, { useCallback, useEffect, useState } from "react";
import { Loader2, History, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getIntegrationsAudit } from "@/lib/api";
import { toast } from "sonner";

const ACTION_STYLE = {
  reauth:         { color: "#0B2545", bg: "rgba(11,37,69,0.06)" },
  secret_set:     { color: "#7A5E12", bg: "rgba(212,175,55,0.18)" },
  secret_cleared: { color: "#9B1C1C", bg: "rgba(155,28,28,0.08)" },
  test_ok:        { color: "#166534", bg: "rgba(22,101,52,0.10)" },
  test_failed:    { color: "#9B1C1C", bg: "rgba(155,28,28,0.08)" },
};

function fmt(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

export default function AuditLogPanel({ reauthToken }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getIntegrationsAudit(reauthToken, 100);
      setEntries(res.entries || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load audit log");
    } finally {
      setLoading(false);
    }
  }, [reauthToken]);

  useEffect(() => { if (reauthToken) load(); }, [reauthToken, load]);

  return (
    <div className="rounded-xl border bg-white" style={{ borderColor: "#E2E8F0" }} data-testid="audit-log-panel">
      <div className="px-5 py-3.5 border-b flex items-center justify-between" style={{ borderColor: "#E2E8F0" }}>
        <div className="flex items-center gap-2">
          <History className="h-4 w-4" style={{ color: "#7A5E12" }} />
          <div className="font-display text-sm font-semibold" style={{ color: "#0B2545" }}>
            Audit trail — most recent 100
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="h-7 text-xs">
          {loading ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1.5" />}
          Refresh
        </Button>
      </div>

      <div className="divide-y" style={{ borderColor: "#E2E8F0" }}>
        {loading && entries.length === 0 ? (
          <div className="px-5 py-10 grid place-items-center text-xs" style={{ color: "#64748B" }}>
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : entries.length === 0 ? (
          <div className="px-5 py-10 text-center text-xs" style={{ color: "#64748B" }}>
            No activity yet.
          </div>
        ) : entries.map((e, i) => {
          const s = ACTION_STYLE[e.action] || ACTION_STYLE.reauth;
          return (
            <div key={i} className="px-5 py-3 flex items-center gap-3 text-xs" data-testid={`audit-row-${i}`}>
              <span
                className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-medium shrink-0"
                style={{ background: s.bg, color: s.color }}
              >
                {e.action.replace(/_/g, " ")}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate" style={{ color: "#0B2545" }}>
                  <span className="font-semibold">{e.user_id || "—"}</span>
                  <span style={{ color: "#64748B" }}> · {e.service}</span>
                  {e.key && <span className="font-mono" style={{ color: "#64748B" }}> · {e.key}</span>}
                </div>
                <div style={{ color: "#94A3B8" }} className="text-[10px]">
                  {fmt(e.ts)}{e.ip ? ` · IP ${e.ip}` : ""}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
